import * as M from '@mantine/core';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  createProdoqCompanyUpdatePayload,
  getProdoqSupportedLanguageOptions,
  normalizeProdoqSupportedLanguages,
} from '../utils/companyInfo';
import usePatchProdoqCompany from '../hooks/usePatchProdoqCompany';

import type { IProdoqCompanyResponse, IProdoqSupportedLanguage } from '../../../../../shared/types/prodoq';

type LimitInputValue = number | string;

interface ICompanyInfoFormFields {
  feedbackManageYn: boolean;
  dictionaryLimit: LimitInputValue;
  pageLimit: LimitInputValue;
  accountLimit: LimitInputValue;
  dailyChatTurnLimit: LimitInputValue;
  supportedLanguageValues: string[];
}

interface ICompanyInfoModalProps {
  opened: boolean;
  onClose: () => void;
  selectedCompany: IProdoqCompanyResponse | null;
}

const validateLimitValue = (value: LimitInputValue): true | string => {
  const numericValue = Number(value);

  if (value === '' || !Number.isFinite(numericValue)) {
    return '값을 입력해주세요.';
  }

  if (!Number.isInteger(numericValue)) {
    return '정수로 입력해주세요.';
  }

  if (numericValue < 0) {
    return '0 이상의 숫자를 입력해주세요.';
  }

  return true;
};

const isProdoqSupportedLanguage = (language: IProdoqSupportedLanguage | undefined): language is IProdoqSupportedLanguage => {
  return !!language;
};

const getCompanyInfoFormValues = (company: IProdoqCompanyResponse): ICompanyInfoFormFields => ({
  feedbackManageYn: company.feedbackManageYn === 'Y',
  dictionaryLimit: company.dictionaryLimit ?? 0,
  pageLimit: company.pageLimit ?? 0,
  accountLimit: company.accountLimit ?? 0,
  dailyChatTurnLimit: company.dailyChatTurnLimit ?? 0,
  supportedLanguageValues: normalizeProdoqSupportedLanguages(company.supportedLanguages).map((language) => language.value),
});

export default function CompanyInfoModal(props: ICompanyInfoModalProps) {
  const { opened, onClose, selectedCompany } = props;
  const supportedLanguageOptions = getProdoqSupportedLanguageOptions(selectedCompany?.supportedLanguages ?? []);
  const { mutate: patchProdoqCompany, isLoading } = usePatchProdoqCompany();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ICompanyInfoFormFields>({
    defaultValues: {
      feedbackManageYn: false,
      dictionaryLimit: 0,
      pageLimit: 0,
      accountLimit: 0,
      dailyChatTurnLimit: 0,
      supportedLanguageValues: [],
    },
  });

  const handleClose = () => {
    if (selectedCompany) {
      reset(getCompanyInfoFormValues(selectedCompany));
    }

    onClose();
  };

  const handleSubmitCompanyInfo = (data: ICompanyInfoFormFields) => {
    if (!selectedCompany) {
      return;
    }

    const supportedLanguageMap = new Map(supportedLanguageOptions.map((language) => [language.value, language]));
    const supportedLanguages = data.supportedLanguageValues
      .map((value) => supportedLanguageMap.get(value))
      .filter(isProdoqSupportedLanguage);

    patchProdoqCompany(
      {
        companyId: selectedCompany.companyId,
        data: createProdoqCompanyUpdatePayload(selectedCompany, {
          pageLimit: Number(data.pageLimit),
          accountLimit: Number(data.accountLimit),
          dictionaryLimit: Number(data.dictionaryLimit),
          dailyChatTurnLimit: Number(data.dailyChatTurnLimit),
          feedbackManageYn: data.feedbackManageYn ? 'Y' : 'N',
          supportedLanguages,
        }),
      },
      {
        onSuccess: onClose,
      },
    );
  };

  useEffect(() => {
    if (!opened || !selectedCompany) {
      return;
    }

    reset(getCompanyInfoFormValues(selectedCompany));
  }, [opened, reset, selectedCompany]);

  return (
    <M.Modal opened={opened} onClose={handleClose} title="회사 설정" centered size="lg">
      <form onSubmit={handleSubmit(handleSubmitCompanyInfo)}>
        <M.Stack gap="md">
          <M.TextInput label="회사" readOnly disabled value={selectedCompany?.name ?? ''} />
          <Controller
            name="feedbackManageYn"
            control={control}
            render={({ field }) => (
              <M.Group gap="sm">
                <M.Text size="sm" fw={500}>
                  피드백 관리
                </M.Text>
                <M.Switch checked={field.value} onChange={(event) => field.onChange(event.currentTarget.checked)} />
              </M.Group>
            )}
          />
          <M.SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Controller
              name="dictionaryLimit"
              control={control}
              rules={{ validate: validateLimitValue }}
              render={({ field }) => (
                <M.NumberInput
                  withAsterisk
                  label="사전 제한 수"
                  min={0}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.dictionaryLimit?.message}
                />
              )}
            />
            <Controller
              name="pageLimit"
              control={control}
              rules={{ validate: validateLimitValue }}
              render={({ field }) => (
                <M.NumberInput
                  withAsterisk
                  label="페이지 제한 수"
                  min={0}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.pageLimit?.message}
                />
              )}
            />
            <Controller
              name="accountLimit"
              control={control}
              rules={{ validate: validateLimitValue }}
              render={({ field }) => (
                <M.NumberInput
                  withAsterisk
                  label="계정 제한 수"
                  min={0}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.accountLimit?.message}
                />
              )}
            />
            <Controller
              name="supportedLanguageValues"
              control={control}
              rules={{
                validate: (value) => (value.length > 0 ? true : '지원 언어를 선택해주세요.'),
              }}
              render={({ field }) => (
                <M.MultiSelect
                  withAsterisk
                  label="지원 언어"
                  data={supportedLanguageOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.supportedLanguageValues?.message}
                />
              )}
            />
          </M.SimpleGrid>
          <Controller
            name="dailyChatTurnLimit"
            control={control}
            rules={{ validate: validateLimitValue }}
            render={({ field }) => (
              <M.NumberInput
                withAsterisk
                label="일일 채팅 제한량"
                description="0은 무제한입니다."
                min={0}
                value={field.value}
                onChange={field.onChange}
                error={errors.dailyChatTurnLimit?.message}
              />
            )}
          />
        </M.Stack>

        <M.Group justify="center" mt="xl">
          <M.Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            취소
          </M.Button>
          <M.Button type="submit" color="secondary" loading={isLoading}>
            저장
          </M.Button>
        </M.Group>
      </form>
    </M.Modal>
  );
}
